<template>
	<div>
    <b-card v-if="amISystemAdmin">
      <b-form v-on:submit.prevent="handleSubmit">
        <legend>{{actionType === 'create' ? 'Add Partner' : 'Edit Partner'}}</legend>
        <b-form-group
          id="label_fullname"
          label="Company"
          label-for="input_company_name">
          <multiselect
            name="company"
            v-validate="'required'"
            :class="{'multiselect-danger': errors.has('company')}"
            v-model="form.company"
            :options="partnerOptions"
            label="name"
            track-by="id"
            :disabled="isDisabled"
            placeholder="Select company"/>
        </b-form-group>
        <div class="form-group">
          <label for="input_type">Select Type of Partner</label>
          <b-input-group>
            <b-form-select
              v-model="form.type"
              :options="typeOptions"
              v-validate="'required'"
              class="form-control"
              :class="{'is-invalid': errors.has('type')}"
              name="type"/>
          </b-input-group>
        </div>
        <div class="form-group">
          <label for="date">Access Expiration Date</label>
          <flatpickr
            v-model="form.expiration_date"
            :config="config"
            class="form-control"
            placeholder="Select date"
            name="date">
          </flatpickr>
        </div>
        <b-button type="submit" variant="primary">{{actionType === 'create' ? 'Submit' : 'Save Changes'}}</b-button>
        <b-button
          @click="onCancel()"
          v-if="actionType !== 'create'"
          variant="outline-secondary"
          class="pull-right">Cancel</b-button>
      </b-form>
    </b-card>
    <b-card>
      <div slot="header">
        <div class="mt-2 row no-gutters pull-left">
          Partners of <strong class="ml-1 mr-1">{{displayName}}</strong><b-badge pill variant="dark" class="pt-1">{{totalPartners}}</b-badge>
        </div>
        <div class="form-group row no-gutters mb-0 pull-right">
          <b-input-group class="col-md-6 col-lg-8">
            <b-input-group-text slot="prepend">
              <i class="fa fa-search"></i>
            </b-input-group-text>
            <b-form-input
              type="text"
              placeholder="Search company by name"
              @input="onChangeSearchQuery"
              v-model="searchQuery"></b-form-input>
          </b-input-group>
        <b-input-group class="pl-md-1 col-md-5 col-lg-4">
          <b-form-select
            v-model="sortType"
            @change="onChangeSortType"
            :options="sortOptions"> </b-form-select>
        </b-input-group>
        </div>
      </div>
      <b-list-group class="no-box" v-if="totalPartners">
        <b-list-group-item v-for="partner in displayPartners" :key="partner.company.id">
          <div class="pull-left">
            <router-link :to="'/admin/companies/' + partner.company.id">
              <strong>{{partner.company.name}}</strong>
            </router-link>
            <p class="mb-1">{{partner.type}}</p>
            <small>
              {{ createExpireDate(partner.expiration_date) }}
            </small>
          </div>
          <div class="pull-right" v-if="amISystemAdmin">
            <b-button @click="editPartner(partner.company.id)" variant="outline-primary"><i class="fa fa-pencil-square-o fa-lg"></i>&nbsp; Edit</b-button>
            <b-button v-b-modal.delete_partner @click="deletePartnerId = partner.company.id" variant="outline-danger" class="m-md-2"><i class="fa fa-trash-o fa-lg"></i></b-button>
          </div>
        </b-list-group-item>
      </b-list-group>
      <b-pagination class="mt-3" align="center" size="md" :total-rows="totalPartners" v-model="currentPage" :per-page="query_count" v-if="totalPartners">
      </b-pagination>
      <b-alert show v-else>There's no partner inside this company.</b-alert>
    </b-card>
    <div>
      <b-modal
        id="delete_partner"
        title="Delete Partner"
        header-bg-variant="danger"
        ok-variant="danger"
        @ok="deletePartner(deletePartnerId)">
        <p class="my-4">Are you sure want to delete this partner ?</p>
      </b-modal>
    </div>
	</div>
</template>
<script>
import dateMixin from '@/mixins/date';
import userMixin from '@/mixins/user';
import { format } from 'date-fns';
import { chain, find, reduce, isNull, sortBy, filter, omit, throttle } from 'lodash';

import Multiselect from 'vue-multiselect';
import Flatpickr from 'vue-flatpickr-component';

export default {
  name: 'partner_list',
  mixins: [dateMixin, userMixin],
  components: {
    Multiselect,
    Flatpickr,
  },
  data() {
    return {
      loaded: false,
      searchQuery: '',
      deletePartnerId: null,
      currentPage: 1,
      actionType: 'create',
      sortOptions: [
        { value: null, text: 'Sort by', disable: true },
        { value: 'NAME.ASC', text: 'Name A-Z' },
        { value: 'NAME.DESC', text: 'Name Z-A' },
        { value: 'CREATED_AT.ASC', text: 'Newer' },
        { value: 'CREATED_AT.DESC', text: 'Older' },
      ],
      typeOptions: [
        { value: null, text: 'Select type', disable: true },
        { value: 'FULL', text: 'FULL' },
        { value: 'TRANSPORTER', text: 'TRANSPORTER' },
        { value: 'CUSTOMER', text: 'CUSTOMER' },
        { value: 'SUPPLIER', text: 'SUPPLIER' },
      ],
      config: {
        altFormat: 'j F Y',
        altInput: true,
        minDate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
      },
      sortType: null,
      form: {
        type: null,
        company: null,
        expiration_date: null,
      },
      partners: null,
    };
  },
  created() {
    this.$store.dispatch('companies/LOAD_DETAIL_DATA');
    this.$store.commit('companies/CLEAR_QUERY');
    this.$store.dispatch('companies/LOAD_INFO').then(() => {
      this.$store.commit('companies/SET_QUERY_COUNT', this.count_all);
      this.$store.dispatch('companies/LOAD_DATA').then(() => {
        this.loaded = true;
        this.partners = this.detailedInfo.partners || [];
        this.initFromUrl();
      });
    });
  },
  computed: {
    displayName() {
      return this.detailedInfo.name || '';
    },
    companies() {
      return this.$store.getters['companies/companies'];
    },
    detailedInfo() {
      return this.$store.getters['companies/get_detail_data'];
    },
    count_all() {
      return this.$store.getters['companies/count_all'];
    },
    query_count() {
      return this.$store.getters['companies/query_count'];
    },
    partnerOptions() {
      const partnerOptions = chain(this.companies)
        .reject(company => find(this.partners, { company: { id: company.id } }))
        .filter(company => isNull(company.parent))
        .value();
      return partnerOptions;
    },
    totalPartners() {
      return this.partners ? this.partners.length : 0;
    },
    displayPartners() {
      const end = this.currentPage * this.query_count;
      const start = (end - this.query_count) + 1;
      const partners = reduce(this.partners, (arr, value, idx) => {
        const seq = idx + 1;
        if (seq >= start && seq <= end) {
          arr.push(value);
        }
        return arr;
      }, []);
      return partners;
    },
    isDisabled() {
      return this.actionType !== 'create';
    },
  },
  methods: {
    initFromUrl() {
      const { query } = this.$route;

      if (query.sort !== undefined) {
        this.onChangeSortType(query.sort);
      }

      if (query.q !== undefined) {
        this.onChangeSearchQuery(query.q);
      }
    },
    onChangeSearchQuery(newValue) {
      const { query } = this.$route;
      this.updateSearchQuery(this, newValue);
      this.searchQuery = newValue;
      if (newValue) {
        this.$router.push({
          query: {
            ...query,
            q: newValue,
          },
        });
      } else {
        this.$router.push({
          query: {
            ...omit(query, ['q']),
          },
        });
      }
    },
    updateSearchQuery: throttle((context, searchTerm) => {
      const searchQuery = searchTerm.trim();
      if (!searchQuery) {
        context.$store.dispatch('companies/LOAD_DETAIL_DATA').then(() => {
          context.partners = context.detailedInfo.partners || [];
        });
      }
      context.partners = filter(context.partners, partner =>
        partner.company.name.toLowerCase().indexOf(searchQuery.toLowerCase()) > -1);
    }, 500, {
      leading: false,
    }),
    clearForm() {
      this.form = {
        type: null,
        company: null,
        expiration_date: null,
      };
      this.actionType = 'create';
    },
    handleSubmit() {
      this.validate().then((valid) => {
        if (valid) {
          if (this.actionType === 'create') {
            this.createPartner();
          } else {
            this.updatePartner();
          }
        }
      });
    },
    createPartner() {
      this.$store.dispatch('companies/CREATE_PARTNER', this.form).then((company) => {
        this.partners = company.partners;
        this.clearForm();
        // TODO: After Create Partner, Do Sort and Search if exist
      });
    },
    deletePartner(id) {
      this.$store.dispatch('companies/DELETE_PARTNER', id).then((deleted) => {
        if (deleted) {
          this.$store.dispatch('companies/LOAD_DETAIL_DATA').then(() => {
            this.partners = this.detailedInfo.partners || [];
          });
        }
      });
    },
    editPartner(id) {
      const partner = find(this.partners, { company: { id } });
      this.form = { ...partner };
      this.actionType = 'edit';
    },
    updatePartner() {
      this.$store.dispatch('companies/UPDATE_PARTNER', this.form).then((updated) => {
        if (updated) {
          this.$store.dispatch('companies/LOAD_DETAIL_DATA').then(() => {
            this.partners = this.detailedInfo.partners || [];
            this.clearForm();
            setTimeout(() => {
              this.errors.items.splice(0);
            }, 10);
          });
        }
      });
    },
    validate() {
      return this.$validator.validateAll();
    },
    onCancel() {
      this.clearForm();
      setTimeout(() => {
        this.errors.items.splice(0);
      }, 10);
    },
    onChangeSortType(newValue) {
      const { query } = this.$route;

      if (newValue) {
        this.$router.push({
          query: {
            ...query,
            sort: newValue,
          },
        });
        this.sortType = newValue;
        const sortType = newValue.split('.');
        const [by, dir] = sortType;
        const partners = sortBy(this.partners, [(p) => {
          if (by === 'NAME') {
            return p.company.name;
          }
          return p.expiration_date;
        }]);
        this.partners = dir === 'ASC' ? partners : partners.reverse();
      } else {
        this.$router.push({
          query: {
            ...omit(query, ['sort']),
          },
        });
      }
    },
    createExpireDate(date) {
      if (date === null) {
        return '';
      }
      const unixTime = (new Date(date)).getTime();
      const now = Date.now();

      if (unixTime - now) {
        return 'Expired';
      }

      return this.formatDateDistance(date);
    },
  },
};
</script>
<style lang="sass">
@import "~flatpickr/dist/flatpickr.min"
@import "~vue-multiselect/dist/vue-multiselect.min"
.multiselect
  z-index: 5;
.multiselect-danger .multiselect__tags
  border: 1px solid #f86c6b;
</style>



// WEBPACK FOOTER //
// src/views/pages/admin/companies/detail/Partners.vue